import { idGet } from "@gameApp/helpers/id";
import type { Dispatch, SetStateAction } from "react";
import { useEffect, useRef, useState } from "react";

type UpdateValue = Record<string, never>;

type Updater = Dispatch<SetStateAction<UpdateValue>>;

type Dispatcher<T, A> = (
  type: A,
  payload: Partial<T>,
  silent?: boolean,
) => void;

type Subscriber<T> = (prevState: T, nextState: T) => void;

interface StoreResult<T, A> {
  getState: () => T;
  dispatch: Dispatcher<T, A>;
  subscribe: (subscriber: Subscriber<T>) => () => void;
  useStore: () => void;
}

type StoreReducer<T, A> = (
  prevState: T,
  action: { type: A; payload: Partial<T> },
) => T;

export function storeCreate<T, A = string>(
  initialState: T,
  reducer: StoreReducer<T, A>,
): StoreResult<T, A> {
  let state = initialState;
  const updaters = new Map<string, Updater>();
  const subscribers = new Set<Subscriber<T>>();

  function useStore() {
    const keyRef = useRef(idGet(8));
    const [, forceRender] = useState<UpdateValue>({});

    useEffect(() => {
      const key = keyRef.current;
      updaters.set(key, forceRender);

      return () => {
        updaters.delete(key);
      };
    }, []);
  }

  function getState() {
    return state;
  }

  function dispatch(type: A, payload: Partial<T>, silent = false) {
    const prevState = state;

    const nextState = reducer(state, {
      type,
      payload,
    });

    state = nextState;

    if (silent === false) {
      if (nextState !== prevState) {
        // biome-ignore lint/complexity/noForEach: <explanation>
        updaters.forEach((updater) => {
          updater({});
        });
      }

      // biome-ignore lint/complexity/noForEach: <explanation>
      subscribers.forEach((subscriber) => {
        subscriber(prevState, nextState);
      });
    }
  }

  function subscribe(subscriber: Subscriber<T>): () => void {
    subscribers.add(subscriber);

    return () => {
      subscribers.delete(subscriber);
    };
  }

  return {
    getState,
    dispatch,
    subscribe,
    useStore,
  };
}
